import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

/**
 * destinosFilter — interactive prototype for the ITD-84554 destinations
 * include/exclude filter (Portfolio Clientes).
 *
 * Implements the two-column Figma design (frames 4/6/7): a searchable pop-up
 * with a "no incluidos" column on the left and an "incluidos" column on the
 * right. The predictive search filters BOTH columns, type chips filter the
 * lists and show how many selected destinations fall under each level, and the
 * footer reflects how many destinations were added in the session.
 *
 * Generic by design: it reads its dataset from a JSON <script> inside the host
 * element, so the markup carries the data and this plugin stays reusable.
 */

type DestinoTipo = "pais" | "comunidad" | "provincia" | "ciudad" | "zona";

interface Destino {
  id: string;
  nombre: string;
  tipo: DestinoTipo;
  ruta?: string;
  preincluido?: boolean;
}

const TIPO_ICON: Record<DestinoTipo, string> = {
  pais: `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 14c2.206 0 4-1.794 4-4s-1.794-4-4-4s-4 1.794-4 4s1.794 4 4 4m0-6c1.103 0 2 .897 2 2s-.897 2-2 2s-2-.897-2-2s.897-2 2-2"/><path fill="currentColor" d="M11.42 21.814a1 1 0 0 0 1.16 0C12.884 21.599 20.029 16.44 20 10c0-4.411-3.589-8-8-8S4 5.589 4 9.995c-.029 6.445 7.116 11.604 7.42 11.819M12 4c3.309 0 6 2.691 6 6.005c.021 4.438-4.388 8.423-6 9.73c-1.611-1.308-6.021-5.294-6-9.735c0-3.309 2.691-6 6-6"/></svg>`,
  comunidad: `<svg viewBox="0 0 48 48" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M16.445 6.168a1 1 0 0 1 1.11 0l6 4A1 1 0 0 1 24 11v8a1 1 0 0 1-1 1H11a1 1 0 0 1-1-1v-8a1 1 0 0 1 .445-.832zM16 18h2v-4h-2zm4 0v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5h-2v-6.465l5-3.333l5 3.333V18zm14.496-5.868a1 1 0 0 0-.992 0l-7 4A1 1 0 0 0 26 17v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V17a1 1 0 0 0-.504-.868zM37 26h3v-8.42l-6-3.428l-6 3.428V26h3v-6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1zm-2 0v-5h-2v5zm-11.553 2.106l-8-4a1 1 0 0 0-.894 0l-8 4A1 1 0 0 0 6 29v12a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V29a1 1 0 0 0-.553-.894M16 34v6h-2v-6zm2-1v7h4V29.618l-7-3.5l-7 3.5V40h4v-7a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1" clip-rule="evenodd"/></svg>`,
  provincia: `<svg viewBox="0 0 24 24" aria-hidden="true"><g fill="none"><path stroke="currentColor" stroke-linecap="square" stroke-width="2" d="M12 21H3V3h4l5 5.5zm0 0V3h4l5 6v12z"/><path stroke="currentColor" stroke-linecap="square" stroke-width="2" d="M16 8.998h.004v.004H16zm0 4h.004v.004H16zm0 4h.004v.004H16zm-9-8h.004v.004H7zm0 4h.004v.004H7zm0 4h.004v.004H7z"/></g></svg>`,
  ciudad: `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m7 9.01l.01-.011M11 9.01l.01-.011M7 13.01l.01-.011m3.99.011l.01-.011M7 17.01l.01-.011m3.99.011l.01-.011M15 21H3.6a.6.6 0 0 1-.6-.6V5.6a.6.6 0 0 1 .6-.6H9V3.6a.6.6 0 0 1 .6-.6h4.8a.6.6 0 0 1 .6.6V9m0 12h5.4a.6.6 0 0 0 .6-.6V9.6a.6.6 0 0 0-.6-.6H15m0 12v-4m0-8v4m0 0h2m-2 0v4m0 0h2"/></svg>`,
  zona: `<svg viewBox="0 0 24 24" aria-hidden="true"><g fill="none"><path stroke="currentColor" stroke-linecap="square" stroke-width="2" d="M15.5 11V5.5a3.5 3.5 0 1 0-7 0V11M4 8h16v14H4z"/></g></svg>`,
};

const ICON_OUT = `<svg viewBox="0 0 24 24" aria-hidden="true"><g fill="none"><path fill="currentColor" d="M3 3V2H2v1zm9.293 10.707a1 1 0 0 0 1.414-1.414zM4 11V3H2v8zM3 4h8V2H3zm-.707-.293l10 10l1.414-1.414l-10-10z"/><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M4 15c0 1.87 0 2.804.402 3.5A3 3 0 0 0 5.5 19.598C6.196 20 7.13 20 9 20h5c2.828 0 4.243 0 5.121-.879C20 18.243 20 16.828 20 14V9c0-1.87 0-2.804-.402-3.5A3 3 0 0 0 18.5 4.402C17.804 4 16.87 4 15 4"/></g></svg>`;
const ICON_IN = `<svg viewBox="0 0 24 24" aria-hidden="true"><g fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M3 9v6c0 2.828 0 4.243.879 5.121C4.757 21 6.172 21 9 21h6c2.828 0 4.243 0 5.121-.879C21 19.243 21 17.828 21 15V9c0-2.828 0-4.243-.879-5.121C19.243 3 17.828 3 15 3H9"/><path fill="currentColor" d="M15 15v1h1v-1zM7.707 6.293a1 1 0 0 0-1.414 1.414zM14 8v7h2V8zm1 6H8v2h7zm.707.293l-8-8l-1.414 1.414l8 8z"/></g></svg>`;

const TIPOS: DestinoTipo[] = [
  "pais",
  "comunidad",
  "provincia",
  "ciudad",
  "zona",
];

/** Lowercase + strip diacritics so "Mádrid" matches "madrid". */
function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export class DestinosFilter implements Plugin {
  private destinos: Destino[] = [];
  private included = new Set<string>();
  private initialIncluded = new Set<string>();
  private query = "";
  private activeType: DestinoTipo | "todo" = "todo";

  private searchInput: HTMLInputElement | null = null;
  private listExcluidos: HTMLElement | null = null;
  private listIncluidos: HTMLElement | null = null;
  private counter: HTMLElement | null = null;
  private incluirBtn: HTMLButtonElement | null = null;
  private readonly chips = new Map<DestinoTipo | "todo", HTMLElement>();
  private openTriggers: HTMLElement[] = [];

  private readonly onSearch = () => {
    this.query = this.searchInput?.value ?? "";
    this.renderLists();
  };
  private readonly onListClick = (e: Event) => this.handleListClick(e);
  private readonly onChipClick = (e: Event) => this.handleChipClick(e);
  private readonly onRootClick = (e: Event) => this.handleRootClick(e);
  private readonly onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") this.close();
  };
  private readonly onOpen = () => this.open();

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.destinos = this.readData();
    if (!this.destinos.length) return;

    this.destinos.forEach((d) => {
      if (d.preincluido) {
        this.included.add(d.id);
        this.initialIncluded.add(d.id);
      }
    });

    this.searchInput = this.element.querySelector("[data-df-search]");
    this.listExcluidos = this.element.querySelector(
      '[data-df-list="excluidos"]',
    );
    this.listIncluidos = this.element.querySelector(
      '[data-df-list="incluidos"]',
    );
    this.counter = this.element.querySelector("[data-df-counter]");
    this.incluirBtn = this.element.querySelector('[data-df-action="incluir"]');

    this.element
      .querySelectorAll<HTMLElement>("[data-df-chip]")
      .forEach((chip) => {
        const tipo = (chip.dataset.tipo as DestinoTipo | "todo") || "todo";
        this.chips.set(tipo, chip);
      });

    this.searchInput?.addEventListener("input", this.onSearch);
    this.listExcluidos?.addEventListener("click", this.onListClick);
    this.listIncluidos?.addEventListener("click", this.onListClick);
    this.element
      .querySelector("[data-df-chips]")
      ?.addEventListener("click", this.onChipClick);
    this.element.addEventListener("click", this.onRootClick);
    document.addEventListener("keydown", this.onKeydown);

    const openSelector = this.element.dataset.dfOpenTrigger;
    if (openSelector) {
      this.openTriggers = Array.from(
        document.querySelectorAll<HTMLElement>(openSelector),
      );
      this.openTriggers.forEach((t) =>
        t.addEventListener("click", this.onOpen),
      );
    }

    this.render();
  }

  destroy(): void {
    this.searchInput?.removeEventListener("input", this.onSearch);
    this.listExcluidos?.removeEventListener("click", this.onListClick);
    this.listIncluidos?.removeEventListener("click", this.onListClick);
    this.element
      .querySelector("[data-df-chips]")
      ?.removeEventListener("click", this.onChipClick);
    this.element.removeEventListener("click", this.onRootClick);
    document.removeEventListener("keydown", this.onKeydown);
    this.openTriggers.forEach((t) =>
      t.removeEventListener("click", this.onOpen),
    );
  }

  open(): void {
    this.element.classList.add("is-open");
    this.element.setAttribute("aria-hidden", "false");
    this.searchInput?.focus();
  }

  close(): void {
    this.element.classList.remove("is-open");
    this.element.setAttribute("aria-hidden", "true");
  }

  private readData(): Destino[] {
    const script =
      this.element.querySelector<HTMLScriptElement>("[data-df-data]");
    if (!script?.textContent) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? (parsed as Destino[]) : [];
    } catch (error) {
      console.error("destinosFilter: invalid dataset JSON", error);
      return [];
    }
  }

  private handleRootClick(e: Event): void {
    const target = e.target as HTMLElement;
    const action =
      target.closest<HTMLElement>("[data-df-action]")?.dataset.dfAction;

    if (target.closest("[data-df-close]") || action === "cancelar") {
      this.revert();
      this.close();
      return;
    }
    // "Incluir" persists the pending changes (dashed → solid green) and keeps
    // the dialog open so the user sees the new state saved.
    if (action === "incluir") {
      this.commit();
      return;
    }
    // "Finalizar" persists and closes.
    if (action === "finalizar") {
      this.commit();
      this.close();
      return;
    }
    // Click on the backdrop (the host element itself) closes without committing.
    if (target === this.element) {
      this.revert();
      this.close();
    }
  }

  private handleChipClick(e: Event): void {
    const chip = (e.target as HTMLElement).closest<HTMLElement>(
      "[data-df-chip]",
    );
    if (!chip) return;
    this.activeType = (chip.dataset.tipo as DestinoTipo | "todo") || "todo";
    this.renderChips();
    this.renderLists();
  }

  private handleListClick(e: Event): void {
    const row = (e.target as HTMLElement).closest<HTMLElement>("[data-id]");
    if (!row) return;
    const id = row.dataset.id;
    if (!id) return;

    if (this.included.has(id)) {
      this.included.delete(id);
    } else {
      this.included.add(id);
    }
    this.render();
  }

  private revert(): void {
    this.included = new Set(this.initialIncluded);
    this.render();
  }

  private commit(): void {
    this.initialIncluded = new Set(this.included);
    this.render();
  }

  private matches(d: Destino): boolean {
    if (this.activeType !== "todo" && d.tipo !== this.activeType) return false;
    if (!this.query) return true;
    const q = normalize(this.query);
    return (
      normalize(d.nombre).includes(q) || normalize(d.ruta ?? "").includes(q)
    );
  }

  private render(): void {
    this.renderChips();
    this.renderLists();
    this.renderFooter();
  }

  private renderChips(): void {
    const counts: Record<string, number> = { todo: this.included.size };
    TIPOS.forEach((t) => (counts[t] = 0));
    this.destinos.forEach((d) => {
      if (this.included.has(d.id)) counts[d.tipo] = (counts[d.tipo] ?? 0) + 1;
    });

    this.chips.forEach((chip, tipo) => {
      const count = counts[tipo] ?? 0;
      const countEl = chip.querySelector("[data-df-count]");
      if (countEl) countEl.textContent = `(${count})`;
      chip.classList.toggle("is-active", tipo === this.activeType);
      const empty = tipo !== "todo" && count === 0;
      chip.classList.toggle("is-empty", empty);
      chip.setAttribute("aria-pressed", String(tipo === this.activeType));
    });
  }

  private renderLists(): void {
    if (!this.listExcluidos || !this.listIncluidos) return;

    const excluidos: Destino[] = [];
    const incluidos: Destino[] = [];
    this.destinos.forEach((d) => {
      if (!this.matches(d)) return;
      (this.included.has(d.id) ? incluidos : excluidos).push(d);
    });

    this.listExcluidos.innerHTML = excluidos.length
      ? excluidos.map((d) => this.rowTemplate(d, false)).join("")
      : this.emptyTemplate("No hay destinos que coincidan");

    this.listIncluidos.innerHTML = incluidos.length
      ? incluidos.map((d) => this.rowTemplate(d, true)).join("")
      : this.emptyTemplate("Ningún destino seleccionado");
  }

  private renderFooter(): void {
    const added = [...this.included].filter(
      (id) => !this.initialIncluded.has(id),
    ).length;

    if (this.counter) {
      this.counter.textContent = `${added} ${added === 1 ? "añadido" : "añadidos"}`;
    }
    if (this.incluirBtn) {
      this.incluirBtn.textContent =
        added > 0 ? `Incluir (${added})` : "Incluir";
      this.incluirBtn.disabled = added === 0;
    }
  }

  private rowTemplate(d: Destino, isIncluded: boolean): string {
    const icon = TIPO_ICON[d.tipo] ?? "";
    const action = isIncluded ? ICON_IN : ICON_OUT;
    const meta = d.ruta
      ? `<span class="destinos-filter__item-meta">${escapeHtml(d.ruta)}</span>`
      : "";
    // "Pending" rows: included this session but not yet persisted (dashed).
    const added =
      isIncluded && !this.initialIncluded.has(d.id) ? " is-added" : "";
    const variant = isIncluded
      ? "destinos-filter__item--incluido"
      : "destinos-filter__item--excluido";

    return `<li class="destinos-filter__item ${variant}${added}" data-id="${escapeHtml(d.id)}">
      <button class="destinos-filter__item-btn" type="button" aria-label="${isIncluded ? "Quitar" : "Añadir"} ${escapeHtml(d.nombre)}">
        <span class="destinos-filter__item-icon">${icon}</span>
        <span class="destinos-filter__item-text">
          <span class="destinos-filter__item-name">${escapeHtml(d.nombre)}</span>
          ${meta}
        </span>
        <span class="destinos-filter__item-action">${action}</span>
      </button>
    </li>`;
  }

  private emptyTemplate(text: string): string {
    return `<li class="destinos-filter__empty">${escapeHtml(text)}</li>`;
  }
}

PluginRegistry.register("destinosFilter", DestinosFilter);
